// Thin GA4 wrapper.
//
// Dormant until the super-admin sets a Measurement ID in Branding — this
// storefront is resold to multiple clients, so a Measurement ID hardcoded in
// index.html would mix one client's traffic into another's GA4 property (or
// send test-store traffic to a real one). initGA() is only ever called with
// whatever id GET /branding returns for THIS install; every tracking call
// below no-ops safely when GA was never initialized, so callers never need
// to check "is analytics on?" themselves.
const GA4_ID_RE = /^G-[A-Za-z0-9]+$/;

let ready = false;

export function initGA(measurementId) {
  if (ready || !measurementId || typeof window === "undefined") return;
  if (!GA4_ID_RE.test(measurementId)) return; // guard against a malformed value reaching a <script src>

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view: false — this is a single-page app; the default automatic
  // pageview only fires once on first load, so every route change afterwards
  // would go untracked. We send page_view ourselves on route change instead
  // (see trackPageview, called from App.js on every location change).
  window.gtag("config", measurementId, { send_page_view: false });

  ready = true;
}

const send = (name, params) => {
  if (!ready || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
};

export const trackPageview = (path) => {
  send("page_view", {
    page_path: path,
    page_title: document.title,
    page_location: window.location.href,
  });
};

const money = (n) => Math.round(Number(n) || 0);

// One GA4 line item. `product` is whatever shape is already lying around at
// the call site (a raw Product doc, or a cart item's populated product_id) —
// this just normalises the couple of field names that differ between them.
const toItem = (product, quantity = 1) => ({
  item_id: product?._id || product?.id || "",
  item_name: product?.name || "Product",
  price: money(product?.is_on_sale ? product?.sale_price : (product?.sell_price ?? product?.price)),
  quantity,
});

export const trackViewItem = (product) => {
  if (!product) return;
  const item = toItem(product);
  send("view_item", { currency: "KRW", value: item.price, items: [item] });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!product) return;
  const item = toItem(product, quantity);
  send("add_to_cart", { currency: "KRW", value: item.price * quantity, items: [item] });
};

// `cartItems` is the CartContext shape: [{ product_id: <populated product>, quantity }]
export const trackBeginCheckout = (cartItems = [], total = 0) => {
  const items = cartItems.map((ci) => toItem(ci.product_id, ci.quantity));
  send("begin_checkout", { currency: "KRW", value: money(total), items });
};

// orderId becomes GA4's transaction_id, which is also GA4's own de-duplication
// key server-side — calling this again for the same order (e.g. the customer
// refreshes the success page) reports the same transaction_id and GA4 collapses
// it rather than double-counting revenue. The local Set is a same-session
// belt-and-suspenders, not the primary guard (it resets on a full reload).
const purchasedThisSession = new Set();

export const trackPurchase = ({ orderId, total, items = [] }) => {
  if (!orderId || purchasedThisSession.has(orderId)) return;
  purchasedThisSession.add(orderId);
  send("purchase", {
    transaction_id: String(orderId),
    currency: "KRW",
    value: money(total),
    ...(items.length ? { items: items.map((i) => toItem(i.product_id || i, i.quantity)) } : {}),
  });
};
